import React, { useEffect, useRef } from "react";
import * as d3 from "d3";
import "./Styles/AreaChart.css";
import { toLocaleNumber } from "@/utils";
import { useLang } from "@/app/providers";
import { object, array, string } from "prop-types";

type ChartDatum = {
  label: string;
  [key: string]: string | number | undefined;
};

type AreaChartSeries = {
  dataKey: string;
  label?: string;
  color?: string;
  unit?: string;
};

type AreaChartData = {
  dataset?: ChartDatum[];
  series?: AreaChartSeries[];
};

type AreaChartProps = {
  data?: AreaChartData;
  chartId?: string;
  locale?: string;
  color?: string;
  height?: number;
  fillArea?: boolean;
  [key: string]: unknown;
};

export const AreaChart = (props: AreaChartProps) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const locale = useLang();

  useEffect(() => {
    if (!svgRef.current) return;

    // Move data and series inside useEffect to avoid dependency issues
    const data = props?.data?.dataset || [];
    const series = props?.data?.series || [];
    const fillArea = props.fillArea ?? true;

    const keyColors: Record<string, string> = {};
    const keyLabels: Record<string, string> = {};
    const keyUnits: Record<string, string> = {};
    const colors: string[] = [];
    const fields: string[] = [];

    series.map((obj: AreaChartSeries): null => {
      // ...existing code...
      fields.push(obj.dataKey);
      if (obj.color) {
        keyColors[obj.dataKey] = obj.color;
        colors.push(obj.color);
      } else {
        keyColors[obj.dataKey] = "#000";
        colors.push("#000");
      }
      if (obj.label) {
        keyLabels[obj.dataKey] = obj.label;
      } else {
        keyLabels[obj.dataKey] = "";
      }
      if (obj.unit) {
        // keyUnits[obj.dataKey] = ` (${obj.unit})`;
        keyUnits[obj.dataKey] = ` ${obj.unit}`;
      } else {
        keyUnits[obj.dataKey] = "";
      }
      return null;
    });

    d3.select(svgRef.current).selectAll("*").remove();

    // Remove any existing tooltips to avoid duplication
    d3.select(`#tooltip-${props.chartId}`).remove();

    const tooltip = d3
      .select("body")
      .append("div")
      .attr("id", `tooltip-${props.chartId}`)
      .attr("class", "tooltip")
      .style("position", "absolute")
      .style("pointer-events", "none")
      .style("opacity", 0);

    const margin = {
      top: 20,
      right: 30,
      bottom: 40,
      left: 80, // y-axis labels space
    };

    const width = svgRef.current.clientWidth - margin.left - margin.right;
    const height = 300 - margin.top - margin.bottom;

    const svg = d3
      .select(svgRef.current)
      .attr("width", width + margin.left + margin.right)
      .attr("height", height + margin.top + margin.bottom)
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    const barWidth = 0;
    const gapWidth = 5;

    const x = d3
      .scaleBand()
      .domain(data.map((d: ChartDatum) => d.label))
      .range([0, width])
      .paddingInner(gapWidth / (barWidth + gapWidth))
      .paddingOuter(barWidth / (barWidth + gapWidth))
      .align(0.5);

    // const x = d3.scaleTime()
    //   .domain(data.map(d => d.label))
    //   .range([margin.left, width - margin.right]);

    const y = d3
      .scaleLinear()
      .domain([
        0,
        d3.max(
          data,
          (d: ChartDatum) =>
            Math.max(...fields.map((field) => Number(d[field] ?? 0))) + 0.01,
        ),
      ])
      .nice()
      .range([height, 0]);

    const color = d3.scaleOrdinal().domain(fields).range(colors);

    const line = d3
      .line()
      .x(
        (d: { label: string; value: number }) =>
          (x(d.label) ?? 0) + x.bandwidth() / 2,
      )
      .y((d: { label: string; value: number }) => y(d.value));

    const area = d3
      .area()
      .x(
        (d: { label: string; value: number }) =>
          (x(d.label) ?? 0) + x.bandwidth() / 2,
      )
      .y0(y(0))
      .y1((d: { label: string; value: number }) => y(d.value));

    // Add horizontal grid lines
    svg
      .append("g")
      .attr("class", "grid")
      .attr("color", "#e5e7eb")
      .call(d3.axisLeft(y).tickSize(-width).tickFormat(""));

    // Add vertical grid lines
    svg
      .append("g")
      .attr("class", "grid")
      .attr("color", "#e5e7eb")
      .call(d3.axisBottom(x).tickSize(-height).tickFormat(""))
      .attr("transform", `translate(0,${height})`);

    fields.forEach((field) => {
      const lineData = data.map((d) => ({
        label: d.label,
        value: d[field] || 0,
      }));

      // Add the area
      if (fillArea) {
        svg
          .append("path")
          .datum(lineData)
          .attr("fill", color(field))
          .attr("fill-opacity", 0.1) // Adjust the opacity for semi-transparency
          .attr("stroke", "none")
          .attr("d", area);
      }

      // Add the line
      svg
        .append("path")
        .datum(lineData)
        .attr("fill", "none")
        .attr("stroke", color(field))
        .attr("stroke-width", 1)
        .attr("d", line);

      svg
        .selectAll(`circle-${field}`)
        .data(lineData)
        .enter()
        .append("circle")
        .attr("class", `circle-${field}`)
        .attr(
          "cx",
          (d: { label: string; value: number }) =>
            (x(d.label) ?? 0) + x.bandwidth() / 2,
        )
        .attr("cy", (d: { label: string; value: number }) => y(d.value))
        .attr("r", 4)
        .attr("stroke", color(field))
        .attr("stroke-width", 1)
        .attr("fill", "#fff");
      // .attr('fill', color(field));
    });

    svg
      .append("g")
      .attr("class", "axis")
      .attr("transform", `translate(0,${height})`)
      .call(
        d3.axisBottom(x).tickFormat((d: string, i: number) => {
          const labelLength = d.length;
          const chartWidth = svgRef.current.clientWidth - 100;
          const extraMarginCharacters = data.length * 2;
          const totalCharacters = data.length * labelLength;
          const pixelPerCharacter = chartWidth / totalCharacters;
          const oneLabelPixels = pixelPerCharacter * labelLength;
          const maxLabels =
            Math.ceil(chartWidth / (oneLabelPixels + extraMarginCharacters)) -
            1;

          return data.length > 12 &&
            i % Math.ceil(data.length / maxLabels) !== 0
            ? ""
            : d;
        }),
      );

    svg.append("g").attr("class", "axis").call(d3.axisLeft(y));

    // Vertical line
    const verticalLine = svg
      .append("line")
      .attr("class", "vertical-line")
      .attr("stroke", "black")
      .attr("y1", 0)
      .attr("y2", height)
      .style("opacity", 0);

    // Overlay for capturing mouse movements
    svg
      .append("rect")
      .attr("width", width)
      .attr("height", height)
      .attr("opacity", 0)
      .on("mousemove", function (event: MouseEvent) {
        const tooltipWidth = 200;
        const mouseX = d3.pointer(event)[0];

        // const mouseX = event.pageX;
        const mouseY = event.pageY;

        if (!data.length) return;

        const x0 = Math.round(mouseX / (width / Math.max(1, data.length)));
        const d = data[Math.min(data.length - 1, Math.max(0, Math.round(x0)))];

        const tooltipX = event.pageX + 15;
        // const tooltipY = event.pageY - 28;

        // const tooltipX = mouseX + 15;
        // const tooltipX = mouseX;
        const tooltipY = mouseY - 28;

        if (!d) return;

        verticalLine
          .attr("x1", x(d.label) + x.bandwidth() / 2)
          .attr("x2", x(d.label) + x.bandwidth() / 2)
          .style("opacity", 1);

        const tooltipContent = `
          <div style="padding: 0; margin: 0">
            <div style="text-align: left; font-weight: bold; padding: 10px;">
              ${d.label}
            </div>
            <div style="border-bottom: 1px solid #ccc"></div>
            ${fields
              .map(
                (field: string) => `
              <div style="text-align: left; padding: 10px;">
                <div style="float: left; height: 12px; width: 12px; margin-right: 10px; background-color:${color(field)}; border-radius: 100%;"></div>
                ${keyLabels[field]}: ${toLocaleNumber(locale, d[field] || 0)}${keyUnits[field]}
                <!-- ${keyLabels[field]}: <span style="color:${color(field)}">${toLocaleNumber(locale, d[field] || 0)}${keyUnits[field]}</span> -->
              </div>
            `,
              )
              .join("")}
          </div>
        `;

        tooltip
          .html(tooltipContent)
          .style(
            "left",
            `${Math.min(tooltipX, window.innerWidth - tooltipWidth)}px`,
          )
          .style("top", `${Math.min(tooltipY)}px`)
          // .style('left', `${Math.min(tooltipX, window.innerWidth - 200)}px`)
          // .style('top', `${Math.min(tooltipY, window.innerHeight - 150)}px`)
          .style("opacity", 1);
      })
      .on("mouseout", () => {
        verticalLine.style("opacity", 0);
        tooltip.transition().duration(500).style("opacity", 0);
      });
  }, [locale, props.chartId, props?.data?.dataset, props?.data?.series]);

  return (
    <div
      style={{
        position: "relative",
      }}
    >
      <svg
        ref={svgRef}
        style={{
          width: "100%",
        }}
      ></svg>
    </div>
  );
};

AreaChart.propTypes = {
  data: object,
  series: array,
  chartId: string,
  locale: string,
};
